import { toast } from '@dd3tech/wiggui/Toast'
import {
  removePropertyFromList,
  getLists,
  addPropertyToList,
  propertyInteraction
} from '../../../services'
import { useModalAddListStore } from '../../../store'
import { useDebounce } from '../../../hooks/ds/useDebounce'
import { createList } from './useModalCreateList'
import { useSession } from '@wiggot/auth/hooks/useSession'
import { useEventList } from '../../../../analytics/src/providers/posthog-provider/hooks/use-event-list'

export interface ParamsHandleAddOrDeletePropertyInList {
  type: 'DELETE' | 'ADD'
  listId: string
  onSuccess?: () => void
}

export const useModalAddList = () => {
  const { listAddPropertyAction } = useEventList()
  const { profile } = useSession()
  const modalAddListOrigin = useModalAddListStore(
    (state) => state?.modalAddList.origin
  )
  const modalAddListIsOpen = useModalAddListStore(
    (state) => state?.modalAddList?.isOpen
  )

  const propertyId = useModalAddListStore(
    (state) => state?.modalAddList?.propertyId
  )

  const resetModalAddList = useModalAddListStore((state) => state?.reset)

  const pagination = useModalAddListStore((state) => state?.pagination)
  const setPagination = useModalAddListStore((state) => state?.setPagination)

  const showStepCreateNewList = useModalAddListStore(
    (state) => state?.showStepCreateNewList
  )
  const setShowStepCreateNewList = useModalAddListStore(
    (state) => state?.setShowStepCreateNewList
  )

  const setIsLoadingGetLists = useModalAddListStore(
    (state) => state?.setIsLoadingGetLists
  )

  const setIsLoadingAddOrDeletePropertyInList = useModalAddListStore(
    (state) => state?.setIsLoadingAddOrDeletePropertyInList
  )
  const setIsLoadingCreateNewList = useModalAddListStore(
    (state) => state?.setIsLoadingCreateNewList
  )

  const lists = useModalAddListStore((state) => state?.lists)
  const setLists = useModalAddListStore((state) => state?.setLists)

  const searchValue = useModalAddListStore((state) => state?.searchValue)
  const setSearchValue = useModalAddListStore((state) => state?.setSearchValue)
  const debounceSearchValue = useDebounce(searchValue, 750)

  const nameNewList = useModalAddListStore((state) => state?.nameNewList)
  const setNameNewList = useModalAddListStore((state) => state?.setNameNewList)

  const onClose = () => {
    resetModalAddList()
  }

  const handleOnClick = () => {
    resetModalAddList()
  }

  const fetchsLists = async (
    { page = 1 }: { page?: number },
    hideLoader?: boolean
  ) => {
    try {
      if (!hideLoader) {
        setIsLoadingGetLists(true)
      }
      const { isSuccess, data } = await getLists({
        page,
        perPage: 50,
        search: debounceSearchValue,
        validateProperty: propertyId
      })

      if (isSuccess && data) {
        setPagination({
          page: data?.pagination?.currentPage,
          pages: data?.pagination?.pages,
          total: data?.pagination?.totalItems
        })

        if (lists && page !== 1) {
          setLists([...lists, ...data?.lists])
          return
        }

        setLists(data?.lists)
        return
      }

      setLists([])
    } catch {
      setLists([])
    } finally {
      if (!hideLoader) {
        setIsLoadingGetLists(false)
      }
    }
  }

  const fetchCreateNewList = async () => {
    try {
      setIsLoadingCreateNewList(true)
      const { isSuccess } = await createList({
        name: nameNewList
      })

      if (isSuccess) {
        setNameNewList('')
        setShowStepCreateNewList(false)
        fetchsLists({ page: 1 })
        return
      }

      toast({
        type: 'error',
        title: 'Ocurrió un error al crear la lista'
      })
    } catch {
      toast({
        type: 'error',
        title: 'Ocurrió un error al crear la lista'
      })
    } finally {
      setIsLoadingCreateNewList(false)
    }
  }

  const fetchAddPropertiesInList = async ({
    listId,
    onSuccess
  }: {
    listId: string
    onSuccess?: () => void
  }) => {
    const generateErrorMessage = () => {
      toast({
        type: 'error',
        title: 'Ocurrió un error al agregar las propiedades a la lista'
      })
    }

    setIsLoadingAddOrDeletePropertyInList({
      isLoading: true,
      listId
    })
    try {
      const { isSuccess } = await addPropertyToList({
        propertyId,
        listId
      })

      if (isSuccess) {
        onSuccess?.()
        fetchsLists({ page: pagination.page }, true)
        propertyInteraction({
          propertyLegacyIds: [propertyId],
          link: window.location.href,
          userId: profile?.userId?.toString() || '',
          type: 'ADD_TO_LIST'
        })

        let where: 'list' | 'inventory' | 'leads' | 'search' | 'datasheet' =
          'search'

        if (modalAddListOrigin === 'SEARCH') {
          where = 'search'
        }
        if (modalAddListOrigin === 'PROPERTIES_TABLE') {
          where = 'inventory'
        }
        if (modalAddListOrigin === 'PROPERTY_DETAIL') {
          where = 'datasheet'
        }
        listAddPropertyAction(where)
        return
      }

      generateErrorMessage()
    } catch {
      generateErrorMessage()
    } finally {
      setIsLoadingAddOrDeletePropertyInList({
        isLoading: false,
        listId: ''
      })
    }
  }

  const deletePropertiesFromList = async ({
    listId,
    onSuccess
  }: {
    listId: string
    onSuccess?: () => void
  }) => {
    const generateErrorMessage = () => {
      toast({
        type: 'error',
        title: 'Ocurrió un error al eliminar la propiedad de la lista'
      })
    }

    setIsLoadingAddOrDeletePropertyInList({
      isLoading: true,
      listId
    })
    try {
      const { isSuccess } = await removePropertyFromList({
        propertyId,
        listId
      })

      if (isSuccess) {
        onSuccess?.()
        fetchsLists({ page: pagination.page }, true)
        return
      }

      generateErrorMessage()
    } catch {
      generateErrorMessage()
    } finally {
      setIsLoadingAddOrDeletePropertyInList({
        isLoading: false,
        listId: ''
      })
    }
  }

  const handleAddOrDeletePropertyInList = async ({
    type,
    listId,
    onSuccess
  }: ParamsHandleAddOrDeletePropertyInList) => {
    if (type === 'ADD') {
      await fetchAddPropertiesInList({ listId, onSuccess })
      return
    }

    await deletePropertiesFromList({ listId, onSuccess })
  }

  return {
    createNewList: fetchCreateNewList,
    handleAddOrDeletePropertyInList,
    handleOnClick,
    modalAddListIsOpen,
    nameNewList,
    onClose,
    searchValue,
    setNameNewList,
    setSearchValue,
    showStepCreateNewList,
    debounceSearchValue,
    getLists: fetchsLists
  }
}
